/**
 * Questionnaire API functions - TypeScript version
 * Authentication headers are handled automatically by the axios instance
 */

import { AxiosInstance } from "axios"
import {
  QuestionnaireGetResponse,
  QuestionnaireSaveResponse,
  QuestionnaireCheckResponse,
  QuestionnaireTranslationsGetResponse,
  QuestionnaireTranslationsSaveResponse,
  QuestionnaireAuditGetResponse,
  QuestionnaireAuditVerifyResponse,
  QuestionnaireSnapshotGetResponse,
  QuestionnaireSaveOptions,
  Translation,
} from "types/questionnaire.types"

/**
 * Fetches a questionnaire by ID.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project
 * @returns Promise resolving to the questionnaire string
 * @throws Will throw an error if the request fails
 */
export async function getQuestionnaire(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string
): Promise<QuestionnaireGetResponse> {
  const response = await axiosInstance.get<string>(
    `/questionnaire-get?id=${id}&hint_owner=${owner}`
  )
  return response
}

/**
 * Saves a questionnaire. A checkpoint (`options.checkpoint`) also cuts a version in the project
 * history: its reply says what the history did (`auditStatus`), and an error answer means the
 * text wasn't saved or the version wasn't recorded (the server's message says which).
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param qnr - The questionnaire content
 * @param owner - The owner of the project
 * @param options - `checkpoint` for a save that cuts a version
 * @returns Promise resolving to save confirmation
 * @throws Will throw an error if the request fails
 */
export async function saveQuestionnaire(
  axiosInstance: AxiosInstance,
  id: string,
  qnr: string,
  owner: string,
  options: QuestionnaireSaveOptions = {}
): Promise<QuestionnaireSaveResponse> {
  // Encode qnr into binary using TextEncoder
  const encoder = new TextEncoder()
  const binaryQnr = encoder.encode(qnr)
  const checkpointParam = options.checkpoint ? "&checkpoint=1" : ""

  const response = await axiosInstance.post(
    `/questionnaire-save?id=${id}&hint_owner=${owner}&type=qnr${checkpointParam}`,
    binaryQnr
  )
  return response
}

/**
 * Checks if a questionnaire is valid.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @returns Promise resolving to validation results
 * @throws Will throw an error if the request fails
 */
export async function checkQuestionnaire(
  axiosInstance: AxiosInstance,
  id: string
): Promise<QuestionnaireCheckResponse> {
  const response = await axiosInstance.post(`/questionnaire-check`, { id })
  return response
}

/**
 * Fetches a questionnaire's translations.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project
 * @returns Promise resolving to translations array
 * @throws Will throw an error if the request fails
 */
export async function getQuestionnaireTranslations(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string
): Promise<QuestionnaireTranslationsGetResponse> {
  const response = await axiosInstance.get(
    `/questionnaire-translations-get?id=${id}&hint_owner=${owner}`
  )
  return response
}

/**
 * Saves questionnaire translations.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project
 * @param translations - The translations data
 * @returns Promise resolving to save confirmation
 * @throws Will throw an error if the request fails
 */
export async function saveQuestionnaireTranslations(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  translations: Translation[]
): Promise<QuestionnaireTranslationsSaveResponse> {
  const response = await axiosInstance.post(
    `/questionnaire-translations-save?id=${id}&hint_owner=${owner}&overwrite=true`,
    translations
  )
  return response
}

/**
 * Fetches the project history (its records, each linked to the one before it).
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project
 * @returns Promise resolving to the audit chain (schema, genesisHash, records[])
 */
export async function getQuestionnaireAudit(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string
): Promise<QuestionnaireAuditGetResponse> {
  return axiosInstance.get(
    `/project-audit-get?id=${encodeURIComponent(id)}&hint_owner=${encodeURIComponent(owner)}`
  )
}

/**
 * Has the server check the project history: every record's link to the one before it, its hash
 * and its seal.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project
 * @returns Promise resolving to { valid, recordsChecked, firstBadSeq, problem }, or
 *   { checked: false, reason } when the check couldn't run
 */
export async function verifyQuestionnaireAudit(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string
): Promise<QuestionnaireAuditVerifyResponse> {
  return axiosInstance.get(
    `/project-audit-verify?id=${encodeURIComponent(id)}&hint_owner=${encodeURIComponent(owner)}`
  )
}

/**
 * Fetches a specific version's raw questionnaire content by its rawHash (for restore/preview).
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project
 * @param rawHash - The version's rawHash (the snapshot name)
 * @returns Promise resolving to the raw questionnaire text of that version
 */
export async function getQuestionnaireSnapshot(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  rawHash: string
): Promise<QuestionnaireSnapshotGetResponse> {
  return axiosInstance.get<string>(
    `/questionnaire-snapshot-get?id=${encodeURIComponent(id)}&hint_owner=${encodeURIComponent(
      owner
    )}&name=${encodeURIComponent(rawHash)}`
  )
}
